/**
 * 加密解密工具
 * 注意：请根据实际后端加密配置修改以下参数
 */

// 加密配置（从环境变量读取或使用默认值）
const CRYPTO_CONFIG = {
  // 算法：AES-CBC 或 AES-ECB
  algorithm: import.meta.env.VITE_ENCRYPTION_ALGORITHM || 'AES-CBC',
  // 密钥（从环境变量读取，默认为空）
  key: import.meta.env.VITE_ENCRYPTION_KEY || 'your-secret-key-here-32bytes',
  // 初始化向量（CBC模式需要，ECB模式不需要）
  iv: import.meta.env.VITE_ENCRYPTION_IV || '1234567890123456', // 16字节
  // 密钥格式：'raw', 'jwk', 'pkcs8', 'spki'
  keyFormat: 'raw',
  // 是否启用加密
  enabled: import.meta.env.VITE_ENCRYPTION_ENABLED !== 'false',
  // 调试模式（默认关闭，除非明确开启）
  debug: false // 强制关闭调试日志，避免控制台输出过多
};

// 检查配置
if (CRYPTO_CONFIG.debug) {
  console.log('加密配置:', {
    algorithm: CRYPTO_CONFIG.algorithm,
    enabled: CRYPTO_CONFIG.enabled,
    debug: CRYPTO_CONFIG.debug,
    keyLength: CRYPTO_CONFIG.key?.length || 0,
    ivLength: CRYPTO_CONFIG.iv?.length || 0
  });
}

// 如果启用了加密但没有配置有效密钥，发出警告
if (CRYPTO_CONFIG.enabled && (!CRYPTO_CONFIG.key || CRYPTO_CONFIG.key.includes('your-secret-key'))) {
  console.warn('⚠️ 加密已启用但未配置有效密钥，请设置 VITE_ENCRYPTION_KEY 环境变量');
  console.warn('当前密钥:', CRYPTO_CONFIG.key);
}

// 文本编码/解码器
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/**
 * 获取IV数据（解码Base64或编码字符串）
 * @returns {Uint8Array} IV字节数组
 */
function getIVData() {
  if (!CRYPTO_CONFIG.iv) {
    throw new Error('AES-CBC模式需要初始化向量(IV)');
  }

  try {
    // 尝试Base64解码IV
    const ivData = decodeBase64(CRYPTO_CONFIG.iv);
    if (CRYPTO_CONFIG.debug) {
      console.log(`IV Base64解码成功，长度: ${ivData.length} 字节`);
    }
    return ivData;
  } catch (e) {
    // 如果Base64解码失败，当作普通字符串处理
    if (CRYPTO_CONFIG.debug) {
      console.log(`IV Base64解码失败，当作字符串处理，长度: ${CRYPTO_CONFIG.iv.length} 字符`);
    }
    return textEncoder.encode(CRYPTO_CONFIG.iv);
  }
}

/**
 * Base64解码（支持URL安全的Base64）
 * @param {string} base64 - Base64编码的字符串
 * @returns {Uint8Array} 解码后的字节数组
 */
function decodeBase64(base64) {
  // 处理URL安全的Base64
  const safeBase64 = base64.replace(/-/g, '+').replace(/_/g, '/');
  // 添加padding
  const paddedBase64 = safeBase64.padEnd(safeBase64.length + (4 - safeBase64.length % 4) % 4, '=');

  try {
    const binaryString = atob(paddedBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch (error) {
    console.error('Base64解码失败:', error);
    throw error;
  }
}

/**
 * Base64编码（支持URL安全的Base64）
 * @param {Uint8Array|ArrayBuffer} data - 要编码的字节数组
 * @param {boolean} urlSafe - 是否使用URL安全的Base64（将+/替换为-_）
 * @returns {string} Base64编码的字符串
 */
function encodeBase64(data, urlSafe = false) {
  let bytes;
  if (data instanceof ArrayBuffer) {
    bytes = new Uint8Array(data);
  } else if (ArrayBuffer.isView(data)) {
    bytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  } else {
    throw new TypeError('输入数据必须是Uint8Array或ArrayBuffer');
  }

  let binaryString = '';
  for (let i = 0; i < bytes.length; i++) {
    binaryString += String.fromCharCode(bytes[i]);
  }

  let base64 = btoa(binaryString);

  if (urlSafe) {
    base64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  return base64;
}

/**
 * 导入密钥
 * @returns {Promise<CryptoKey>}
 */
async function importKey() {
  try {
    // 解码Base64密钥
    let keyData;
    if (CRYPTO_CONFIG.key) {
      // 尝试Base64解码
      try {
        keyData = decodeBase64(CRYPTO_CONFIG.key);
        if (CRYPTO_CONFIG.debug) {
          console.log(`密钥Base64解码成功，长度: ${keyData.length} 字节 (${keyData.length * 8} 位)`);
          console.log(`密钥Hex预览: ${Array.from(keyData.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join('')}...`);
        }
      } catch (e) {
        // 如果Base64解码失败，当作普通字符串处理
        console.warn('密钥Base64解码失败，当作普通字符串处理');
        keyData = textEncoder.encode(CRYPTO_CONFIG.key);
      }
    } else {
      throw new Error('未配置加密密钥');
    }

    // 准备算法参数
    let algorithm;
    if (CRYPTO_CONFIG.algorithm === 'AES-ECB') {
      algorithm = { name: 'AES-ECB' };
    } else {
      // AES-CBC模式，需要IV
      algorithm = {
        name: 'AES-CBC',
        iv: getIVData()
      };
    }

    // 根据密钥长度选择算法
    const keyLengthBits = keyData.length * 8;
    const validKeyLengths = [128, 192, 256];
    if (!validKeyLengths.includes(keyLengthBits)) {
      console.warn(`非标准AES密钥长度: ${keyLengthBits} 位，支持: ${validKeyLengths.join(', ')} 位`);
    }

    if (CRYPTO_CONFIG.debug) {
      console.log(`导入密钥，算法: ${algorithm.name}, 密钥长度: ${keyLengthBits} 位`);
    }

    return await crypto.subtle.importKey(
      CRYPTO_CONFIG.keyFormat,
      keyData,
      algorithm,
      false, // 是否可导出
      ['decrypt']
    );
  } catch (error) {
    console.error('导入密钥失败:', error);
    throw error;
  }
}

/**
 * AES解密函数（使用Web Crypto API）
 * @param {string} encryptedBase64 - 加密的Base64文本
 * @returns {Promise<string>} 解密后的文本
 */
export async function decryptAES(encryptedBase64) {
  if (!CRYPTO_CONFIG.enabled) {
    if (CRYPTO_CONFIG.debug) console.warn('加密未启用，直接返回原文本');
    return encryptedBase64;
  }

  try {
    // 检查是否是明显的明文（避免误解密）
    if (encryptedBase64.includes('<form') ||
        encryptedBase64.includes('alipay') ||
        encryptedBase64.includes('wechat') ||
        encryptedBase64.includes('{') ||
        encryptedBase64.includes('[')) {
      if (CRYPTO_CONFIG.debug) console.log('检测到明文特征，直接返回');
      return encryptedBase64;
    }

    // Base64解码（支持URL安全的Base64）
    const encryptedData = decodeBase64(encryptedBase64);

    // 导入密钥
    const key = await importKey();

    // 解密
    let decryptedData;
    if (CRYPTO_CONFIG.algorithm === 'AES-ECB') {
      decryptedData = await crypto.subtle.decrypt(
        { name: 'AES-ECB' },
        key,
        encryptedData
      );
    } else {
      // AES-CBC
      decryptedData = await crypto.subtle.decrypt(
        {
          name: 'AES-CBC',
          iv: getIVData()
        },
        key,
        encryptedData
      );
    }

    // 解码为字符串
    const decryptedText = textDecoder.decode(decryptedData);

    if (CRYPTO_CONFIG.debug) {
      console.log('解密成功，解密后长度:', decryptedText.length);
      console.log('解密后预览:', decryptedText.substring(0, 200) + '...');
    }

    return decryptedText;
  } catch (error) {
    console.error('AES解密失败:', error);
    console.warn('原始数据:', encryptedBase64.substring(0, 100) + '...');

    // 返回原文本，避免阻塞流程
    return encryptedBase64;
  }
}

/**
 * 同步解密函数（用于非异步环境）
 * @param {string} encryptedText - 加密的文本
 * @returns {string} 解密后的文本
 */
export function decryptAESSync(encryptedText) {
  if (!CRYPTO_CONFIG.enabled) {
    if (CRYPTO_CONFIG.debug) console.log('decryptAESSync: 加密未启用，返回原文本');
    return encryptedText;
  }

  if (CRYPTO_CONFIG.debug) console.log(`decryptAESSync: 输入长度=${encryptedText.length}`);

  // 如果已经是明文，直接返回
  if (!isEncrypted(encryptedText)) {
    if (CRYPTO_CONFIG.debug) console.log('decryptAESSync: 不是加密文本，返回原文本');
    return encryptedText;
  }

  if (CRYPTO_CONFIG.debug) console.log('decryptAESSync: 检测到加密文本，但同步解密未实现');

  // 注意：这里需要根据实际加密算法实现同步解密
  // 由于Web Crypto API是异步的，这里只能返回占位符
  console.warn('⚠️ 同步解密未实现，需要使用异步decryptAES函数');
  console.warn('加密文本预览:', encryptedText.substring(0, 100) + '...');

  // 异步解密（注意：这会阻塞）
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    decryptAES(encryptedText).then(decrypted => {
      console.warn('异步解密结果预览:', decrypted.substring(0, 200) + '...');
    }).catch(err => {
      console.error('异步解密失败:', err);
    });
  }

  return encryptedText;
}

/**
 * 检测文本是否已加密
 * @param {string} text - 待检测的文本
 * @returns {boolean} 是否可能是加密文本
 */
export function isEncrypted(text) {
  if (!text || typeof text !== 'string') {
    if (CRYPTO_CONFIG.debug) console.log('isEncrypted: 无效输入');
    return false;
  }

  if (CRYPTO_CONFIG.debug) {
    console.log(`isEncrypted: 检查文本，长度=${text.length}, 预览=${text.substring(0, 100)}...`);
  }

  // 加密文本通常不包含可读的HTML标签或JSON结构
  // 检查是否包含明显的明文特征
  const plaintextIndicators = [
    '<form', '<html', '{', '[', 'http://', 'https://',
    'alipay', 'wechat', 'wxpay', 'body=', 'method=',
    'app_id=', 'sign=', 'charset=',
    // 微信支付参数特征
    'appid', 'partnerid', 'prepayid', 'package', 'noncestr', 'timestamp',
    // 微信prepayid通常以wx开头
    'wx06', 'wx07', 'wx08', 'wx09', 'wx10', 'wx11', 'wx12', 'wx13', 'wx14', 'wx15'
  ];

  for (const indicator of plaintextIndicators) {
    if (text.includes(indicator)) {
      if (CRYPTO_CONFIG.debug) console.log(`isEncrypted: 检测到明文特征 "${indicator}"，认为是明文`);
      return false;
    }
  }

  // 可能是Base64编码（加密文本通常是Base64）
  const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
  const cleanText = text.replace(/\s/g, '');
  if (cleanText.length >= 16 && cleanText.length % 4 === 0 && base64Regex.test(cleanText)) {
    // 检查是否是微信prepayid（通常以wx开头，后面跟着时间戳和随机字符串）
    if (/^wx\d{2}/.test(cleanText) && cleanText.length > 20) {
      if (CRYPTO_CONFIG.debug) console.log('isEncrypted: 检测到微信prepayid特征，认为是明文');
      return false;
    }
    if (CRYPTO_CONFIG.debug) console.log('isEncrypted: 检测到Base64编码，可能是加密文本');
    return true;
  }

  if (CRYPTO_CONFIG.debug) console.log('isEncrypted: 未检测到加密特征');
  return false;
}

/**
 * 自动解密文本（如果检测到加密）
 * @param {string} text - 待处理的文本
 * @returns {Promise<string>} 解密后的文本或原文本
 */
export async function autoDecrypt(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }

  if (isEncrypted(text)) {
    if (CRYPTO_CONFIG.debug) console.log('检测到加密文本，尝试解密...');
    return await decryptAES(text);
  }

  return text;
}

/**
 * 同步自动解密
 * @param {string} text - 待处理的文本
 * @returns {string} 解密后的文本或原文本
 */
export function autoDecryptSync(text) {
  if (!text || typeof text !== 'string') {
    if (CRYPTO_CONFIG.debug) console.log('autoDecryptSync: 无效输入');
    return text;
  }

  if (CRYPTO_CONFIG.debug) console.log(`autoDecryptSync: 处理文本，长度=${text.length}`);

  const encrypted = isEncrypted(text);
  if (CRYPTO_CONFIG.debug) console.log(`autoDecryptSync: isEncrypted=${encrypted}`);

  if (encrypted) {
    if (CRYPTO_CONFIG.debug) console.log('autoDecryptSync: 检测到加密文本，尝试同步解密...');
    return decryptAESSync(text);
  }

  if (CRYPTO_CONFIG.debug) console.log('autoDecryptSync: 返回原文本');
  return text;
}

/**
 * 解密API响应数据
 * @param {object} responseData - API响应数据
 * @returns {Promise<object>} 解密后的数据
 */
export async function decryptResponse(responseData) {
  if (!responseData || typeof responseData !== 'object') {
    return responseData;
  }

  // 深度遍历对象，解密所有字符串字段
  async function deepDecrypt(obj) {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      const results = [];
      for (const item of obj) {
        results.push(await deepDecrypt(item));
      }
      return results;
    }

    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        result[key] = await autoDecrypt(value);
      } else if (value && typeof value === 'object') {
        result[key] = await deepDecrypt(value);
      } else {
        result[key] = value;
      }
    }

    return result;
  }

  return await deepDecrypt(responseData);
}

/**
 * 同步解密API响应数据
 * @param {object} responseData - API响应数据
 * @returns {object} 解密后的数据
 */
export function decryptResponseSync(responseData) {
  if (!responseData || typeof responseData !== 'object') {
    if (CRYPTO_CONFIG.debug) console.log('decryptResponseSync: 无效输入');
    return responseData;
  }

  if (CRYPTO_CONFIG.debug) {
    console.log('decryptResponseSync: 开始解密响应数据', {
      type: typeof responseData,
      keys: Object.keys(responseData),
      preview: JSON.stringify(responseData).substring(0, 200) + '...'
    });
  }

  // 深度遍历对象，解密所有字符串字段
  function deepDecryptSync(obj, path = '') {
    if (!obj || typeof obj !== 'object') {
      if (CRYPTO_CONFIG.debug) console.log(`deepDecryptSync [${path}]: 非对象类型`, typeof obj);
      return obj;
    }

    if (Array.isArray(obj)) {
      if (CRYPTO_CONFIG.debug) console.log(`deepDecryptSync [${path}]: 处理数组，长度=${obj.length}`);
      return obj.map((item, index) => deepDecryptSync(item, `${path}[${index}]`));
    }

    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      const currentPath = path ? `${path}.${key}` : key;

      if (typeof value === 'string') {
        if (CRYPTO_CONFIG.debug) console.log(`deepDecryptSync [${currentPath}]: 处理字符串，长度=${value.length}, 预览=${value.substring(0, 50)}...`);
        result[key] = autoDecryptSync(value);
        if (CRYPTO_CONFIG.debug && result[key] !== value) {
          console.log(`deepDecryptSync [${currentPath}]: 文本被修改，新长度=${result[key].length}`);
        }
      } else if (value && typeof value === 'object') {
        if (CRYPTO_CONFIG.debug) console.log(`deepDecryptSync [${currentPath}]: 递归处理对象`);
        result[key] = deepDecryptSync(value, currentPath);
      } else {
        result[key] = value;
      }
    }

    return result;
  }

  const decrypted = deepDecryptSync(responseData);

  if (CRYPTO_CONFIG.debug) {
    console.log('decryptResponseSync: 解密完成', {
      originalKeys: Object.keys(responseData),
      decryptedKeys: Object.keys(decrypted)
    });
  }

  return decrypted;
}

/**
 * 支付宝接口内容解密（专门处理支付宝接口返回的加密数据）
 * @param {string} encryptedBase64 - 支付宝接口返回的Base64加密文本
 * @returns {Promise<string>} 解密后的文本
 */
export async function decryptAlipayContent(encryptedBase64) {
  if (!CRYPTO_CONFIG.enabled) {
    if (CRYPTO_CONFIG.debug) console.warn('加密未启用，直接返回原文本');
    return encryptedBase64;
  }

  try {
    // 支付宝接口可能返回的加密数据通常是Base64编码的AES加密数据
    if (CRYPTO_CONFIG.debug) {
      console.log('支付宝内容解密，输入长度:', encryptedBase64.length);
      console.log('输入预览:', encryptedBase64.substring(0, 50) + '...');
    }

    // 直接使用现有的AES解密函数
    const decryptedText = await decryptAES(encryptedBase64);

    if (CRYPTO_CONFIG.debug) {
      console.log('支付宝内容解密成功，解密后长度:', decryptedText.length);
      console.log('解密后预览:', decryptedText.substring(0, 200) + '...');
    }

    return decryptedText;
  } catch (error) {
    console.error('支付宝内容解密失败:', error);
    console.warn('原始加密数据:', encryptedBase64.substring(0, 100) + '...');

    // 返回原文本，避免阻塞流程
    return encryptedBase64;
  }
}

/**
 * 同步解密支付宝接口内容
 * @param {string} encryptedText - 支付宝接口返回的加密文本
 * @returns {string} 解密后的文本
 */
export function decryptAlipayContentSync(encryptedText) {
  if (!CRYPTO_CONFIG.enabled) {
    return encryptedText;
  }

  // 如果已经是明文，直接返回
  if (!isEncrypted(encryptedText)) {
    return encryptedText;
  }

  // 注意：这里需要根据实际加密算法实现同步解密
  // 由于Web Crypto API是异步的，这里只能返回占位符
  console.warn('⚠️ 支付宝同步解密未实现，需要使用异步decryptAlipayContent函数');
  console.warn('支付宝加密文本预览:', encryptedText.substring(0, 100) + '...');

  // 异步解密（注意：这会阻塞）
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    decryptAlipayContent(encryptedText).then(decrypted => {
      console.warn('支付宝异步解密结果预览:', decrypted.substring(0, 200) + '...');
    }).catch(err => {
      console.error('支付宝异步解密失败:', err);
    });
  }

  return encryptedText;
}

/**
 * 解密支付宝接口响应（处理整个响应对象）
 * @param {object} responseData - 支付宝接口响应数据
 * @returns {Promise<object>} 解密后的数据
 */
export async function decryptAlipayResponse(responseData) {
  if (!responseData || typeof responseData !== 'object') {
    return responseData;
  }

  // 支付宝接口通常返回 { success: true, data: "加密的Base64字符串" } 格式
  // 或者 { alipay_trade_app_pay_response: "加密的字符串", sign: "..." } 格式

  if (CRYPTO_CONFIG.debug) {
    console.log('支付宝接口响应解密，输入:', responseData);
  }

  // 尝试解密常见的支付宝响应字段
  const decryptedResponse = { ...responseData };

  // 常见支付宝响应字段
  const alipayFields = [
    'data',
    'alipay_trade_app_pay_response',
    'response',
    'result',
    'payInfo',
    'pay_info'
  ];

  for (const field of alipayFields) {
    if (decryptedResponse[field] && typeof decryptedResponse[field] === 'string') {
      try {
        decryptedResponse[field] = await decryptAlipayContent(decryptedResponse[field]);
      } catch (error) {
        console.warn(`支付宝字段 ${field} 解密失败:`, error);
      }
    }
  }

  // 如果响应是字符串类型，直接尝试解密
  if (typeof decryptedResponse === 'string') {
    return await decryptAlipayContent(decryptedResponse);
  }

  if (CRYPTO_CONFIG.debug) {
    console.log('支付宝接口响应解密结果:', decryptedResponse);
  }

  return decryptedResponse;
}

/**
 * 同步解密支付宝接口响应
 * @param {object} responseData - 支付宝接口响应数据
 * @returns {object} 解密后的数据
 */
export function decryptAlipayResponseSync(responseData) {
  if (!responseData || typeof responseData !== 'object') {
    return responseData;
  }

  // 深度遍历对象，解密所有字符串字段（跳过签名字段）
  function deepDecryptAlipaySync(obj) {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(deepDecryptAlipaySync);
    }

    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      // 跳过签名相关字段（不应解密）
      const skipFields = ['sign', 'signature', 'sign_type', 'signType', 'alipay_cert_sn'];
      if (skipFields.includes(key)) {
        result[key] = value;
        continue;
      }

      if (typeof value === 'string') {
        result[key] = decryptAlipayContentSync(value);
      } else if (value && typeof value === 'object') {
        result[key] = deepDecryptAlipaySync(value);
      } else {
        result[key] = value;
      }
    }

    return result;
  }

  return deepDecryptAlipaySync(responseData);
}

export default {
  decryptAES,
  decryptAESSync,
  isEncrypted,
  autoDecrypt,
  autoDecryptSync,
  decryptResponse,
  decryptResponseSync,
  decryptAlipayContent,
  decryptAlipayContentSync,
  decryptAlipayResponse,
  decryptAlipayResponseSync
};